import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { 
  X, 
  Download, 
  FileText, 
  Calendar, 
  Clock, 
  TrendingUp, 
  TrendingDown,
  Loader2,
  CheckCircle,
  AlertCircle,
  Sun,
  Umbrella,
  Coffee
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import { autoTable } from "jspdf-autotable";

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function DetailedReportModal({ isOpen, onClose, employee, token }) {
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [periodType, setPeriodType] = useState("month"); // month, week, custom

  useEffect(() => {
    if (isOpen && employee) {
      // Definir período padrão (mês atual)
      const now = new Date();
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      
      setStartDate(firstDay.toISOString().split('T')[0]);
      setEndDate(lastDay.toISOString().split('T')[0]);
      setReportData(null);
    }
  }, [isOpen, employee]);

  const handlePeriodChange = (type) => {
    setPeriodType(type);
    const now = new Date();
    
    if (type === "month") {
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      setStartDate(firstDay.toISOString().split('T')[0]);
      setEndDate(lastDay.toISOString().split('T')[0]);
    } else if (type === "week") {
      const dayOfWeek = now.getDay();
      const diff = now.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
      const monday = new Date(now.setDate(diff));
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      setStartDate(monday.toISOString().split('T')[0]);
      setEndDate(sunday.toISOString().split('T')[0]);
    }
  };

  const fetchReport = async () => {
    if (!startDate || !endDate) {
      toast.error("Selecione as datas do período");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/reports/employee/${employee.id}/detailed?start_date=${startDate}&end_date=${endDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReportData(response.data);
    } catch (error) {
      console.error("Erro ao buscar relatório:", error);
      toast.error(error.response?.data?.detail || "Erro ao gerar relatório");
    } finally {
      setLoading(false);
    }
  };

  const generatePDF = () => {
    if (!reportData) return;

    const doc = new jsPDF();
    const { employee: emp, period, summary, daily_records, generated_at } = reportData;

    // Título
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text("Relatório Detalhado de Horas", 105, 20, { align: "center" });

    // Informações do funcionário
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text(`Funcionário: ${emp.name}`, 14, 35);
    doc.text(`Departamento: ${emp.department || "Não informado"}`, 14, 42);
    doc.text(`CPF: ${emp.cpf || "Não informado"}`, 14, 49);
    doc.text(`Período: ${period.start_formatted} a ${period.end_formatted}`, 14, 56);

    // Resumo
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Resumo", 14, 70);

    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text(`Horas Trabalhadas: ${summary.total_worked_formatted}`, 14, 80);
    doc.text(`Horas Esperadas: ${summary.total_expected_formatted}`, 14, 87);
    
    const balanceColor = summary.balance_positive ? [0, 128, 0] : [200, 0, 0];
    doc.setTextColor(...balanceColor);
    doc.setFont("helvetica", "bold");
    doc.text(`Saldo: ${summary.balance_formatted}`, 14, 94);
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "normal");

    doc.text(`Dias Trabalhados: ${summary.days_worked} de ${summary.days_expected} esperados`, 14, 101);

    // Tabela detalhada
    const tableData = daily_records.map(day => {
      let status = "";
      if (day.status === "feriado") status = "Feriado";
      else if (day.status === "ferias") status = "Férias";
      else if (day.status === "folga") status = "Folga";
      else if (day.status === "em_andamento") status = "Em andamento";

      const entries = day.entries.length > 0 ? day.entries.join("\n") : "-";
      const exits = day.exits.length > 0 ? day.exits.join("\n") : "-";

      return [
        `${day.date_formatted}\n(${day.day_name})`,
        entries,
        exits,
        day.worked_formatted,
        day.expected_hours > 0 ? `${day.expected_hours}h` : "-",
        status
      ];
    });

    autoTable(doc, {
      startY: 110,
      head: [["Data", "Entradas", "Saídas", "Trabalhado", "Esperado", "Obs"]],
      body: tableData,
      theme: "grid",
      headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: "bold" },
      styles: { fontSize: 9, cellPadding: 2 },
      columnStyles: {
        0: { cellWidth: 30 },
        1: { cellWidth: 30 },
        2: { cellWidth: 30 },
        3: { cellWidth: 28 },
        4: { cellWidth: 25 },
        5: { cellWidth: 35 }
      }
    });

    // Rodapé
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128);
      doc.text(
        `Gerado em: ${generated_at} | Página ${i} de ${pageCount}`,
        105,
        doc.internal.pageSize.height - 10,
        { align: "center" }
      );
    }

    // Salvar
    const fileName = `relatorio_${emp.name.replace(/\s+/g, '_')}_${period.start_formatted.replace(/\//g, '-')}_a_${period.end_formatted.replace(/\//g, '-')}.pdf`;
    doc.save(fileName);
    toast.success("PDF gerado com sucesso!");
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "feriado": return <Sun className="w-4 h-4 text-yellow-500" />;
      case "ferias": return <Umbrella className="w-4 h-4 text-blue-500" />;
      case "folga": return <Coffee className="w-4 h-4 text-gray-400" />;
      case "em_andamento": return <Loader2 className="w-4 h-4 text-orange-500 animate-spin" />;
      default: return null;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "feriado": return "Feriado";
      case "ferias": return "Férias";
      case "folga": return "Folga";
      case "em_andamento": return "Em andamento";
      default: return "";
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="bg-sky-100 rounded-lg p-2">
              <FileText className="w-5 h-5 text-sky-600" />
            </div>
            <div>
              <h2 className="font-semibold text-lg text-slate-900">Relatório Detalhado</h2>
              <p className="text-sm text-slate-500">{employee?.name}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Filtros */}
        <div className="p-4 border-b border-slate-100 bg-slate-50">
          <div className="flex flex-wrap gap-4 items-end">
            {/* Tipo de período */}
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Período</Label>
              <div className="flex gap-1">
                <button
                  onClick={() => handlePeriodChange("month")}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    periodType === "month" 
                      ? "bg-sky-500 text-white" 
                      : "bg-white text-slate-600 hover:bg-slate-100 border border-slate-200"
                  }`}
                >
                  Mês
                </button>
                <button
                  onClick={() => handlePeriodChange("week")}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    periodType === "week" 
                      ? "bg-sky-500 text-white" 
                      : "bg-white text-slate-600 hover:bg-slate-100 border border-slate-200"
                  }`}
                >
                  Semana
                </button>
                <button
                  onClick={() => setPeriodType("custom")}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    periodType === "custom" 
                      ? "bg-sky-500 text-white" 
                      : "bg-white text-slate-600 hover:bg-slate-100 border border-slate-200"
                  }`}
                >
                  Personalizado
                </button>
              </div>
            </div>

            {/* Datas */}
            <div className="flex gap-2">
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">De</Label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => { setStartDate(e.target.value); setPeriodType("custom"); }}
                  className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Até</Label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => { setEndDate(e.target.value); setPeriodType("custom"); }}
                  className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm"
                />
              </div>
            </div>

            {/* Botão Gerar */}
            <Button onClick={fetchReport} disabled={loading} className="bg-sky-500 hover:bg-sky-600">
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Gerando...
                </>
              ) : (
                <>
                  <Calendar className="w-4 h-4 mr-2" />
                  Gerar Relatório
                </>
              )}
            </Button>

            {/* Botão PDF */}
            {reportData && (
              <Button onClick={generatePDF} variant="outline" className="border-green-500 text-green-600 hover:bg-green-50">
                <Download className="w-4 h-4 mr-2" />
                Baixar PDF
              </Button>
            )}
          </div>
        </div>

        {/* Conteúdo */}
        <div className="flex-1 overflow-y-auto p-4">
          {!reportData && !loading && (
            <div className="text-center py-12 text-slate-400">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Selecione o período e clique em "Gerar Relatório"</p>
            </div>
          )}

          {loading && (
            <div className="text-center py-12">
              <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin text-sky-500" />
              <p className="text-slate-500">Gerando relatório...</p>
            </div>
          )}

          {reportData && (
            <div className="space-y-6">
              {/* Resumo */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-sky-50 to-sky-100 border-sky-200">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-sky-600 mb-1">
                      <Clock className="w-4 h-4" />
                      <span className="text-xs font-medium">Trabalhadas</span>
                    </div>
                    <p className="text-2xl font-bold text-sky-700">
                      {reportData.summary.total_worked_formatted}
                    </p>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-slate-600 mb-1">
                      <Calendar className="w-4 h-4" />
                      <span className="text-xs font-medium">Esperadas</span>
                    </div>
                    <p className="text-2xl font-bold text-slate-700">
                      {reportData.summary.total_expected_formatted}
                    </p>
                  </CardContent>
                </Card>

                <Card className={`bg-gradient-to-br ${
                  reportData.summary.balance_positive 
                    ? "from-green-50 to-green-100 border-green-200" 
                    : "from-red-50 to-red-100 border-red-200"
                }`}>
                  <CardContent className="p-4">
                    <div className={`flex items-center gap-2 mb-1 ${
                      reportData.summary.balance_positive ? "text-green-600" : "text-red-600"
                    }`}>
                      {reportData.summary.balance_positive ? (
                        <TrendingUp className="w-4 h-4" />
                      ) : (
                        <TrendingDown className="w-4 h-4" />
                      )}
                      <span className="text-xs font-medium">Saldo</span>
                    </div>
                    <p className={`text-2xl font-bold ${
                      reportData.summary.balance_positive ? "text-green-700" : "text-red-700"
                    }`}>
                      {reportData.summary.balance_formatted}
                    </p>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-purple-600 mb-1">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-xs font-medium">Dias</span>
                    </div>
                    <p className="text-2xl font-bold text-purple-700">
                      {reportData.summary.days_worked}/{reportData.summary.days_expected}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Tabela detalhada */}
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Data</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Entrada</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Saída</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Entrada</th>
                      <th className="px-3 py-2 text-left font-medium text-slate-600">Saída</th>
                      <th className="px-3 py-2 text-center font-medium text-slate-600">Trabalhado</th>
                      <th className="px-3 py-2 text-center font-medium text-slate-600">Esperado</th>
                      <th className="px-3 py-2 text-center font-medium text-slate-600">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {reportData.daily_records.map((day, index) => (
                      <tr 
                        key={day.date} 
                        className={`${
                          day.status === "folga" || day.status === "feriado" || day.status === "ferias"
                            ? "bg-slate-50 text-slate-400" 
                            : day.status === "em_andamento"
                            ? "bg-orange-50"
                            : "hover:bg-slate-50"
                        }`}
                      >
                        <td className="px-3 py-2">
                          <div className="font-medium">{day.date_formatted}</div>
                          <div className="text-xs text-slate-400">{day.day_name}</div>
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">
                          {day.entries[0] || "-"}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">
                          {day.exits[0] || "-"}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">
                          {day.entries[1] || "-"}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">
                          {day.exits[1] || "-"}
                        </td>
                        <td className="px-3 py-2 text-center font-medium">
                          {day.worked_formatted}
                        </td>
                        <td className="px-3 py-2 text-center text-slate-500">
                          {day.expected_hours > 0 ? `${day.expected_hours}h` : "-"}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <div className="flex items-center justify-center gap-1">
                            {getStatusIcon(day.status)}
                            <span className="text-xs">{getStatusText(day.status)}</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Rodapé */}
              <div className="text-center text-xs text-slate-400">
                Gerado em: {reportData.generated_at} | Timezone: {reportData.timezone}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
